import React, { useEffect, useState } from 'react';
import { useAuth } from '../../context/useAuth';
import { getLandingSummary, getPendingActions, getRecentActivity } from '../../api/landing';
import { useNavigate } from 'react-router-dom';
import { motion } from 'framer-motion';
import { DynamicIcon } from '../icons';
import { useTimezoneFormatter } from '../../hooks/useTimezoneFormatter';

// --- Stat Card ---
const StatCard = ({ title, value, icon, color, delay, subtext }) => (
    <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.3, delay }}
        className="bg-white dark:bg-gray-800 p-6 rounded-xl shadow-sm border border-gray-100 dark:border-gray-700 hover:shadow-md transition-shadow"
    >
        <div className="flex items-center justify-between mb-4">
            <div className={`p-3 rounded-full bg-${color}-50 text-${color}-600 dark:bg-${color}-900/30 dark:text-${color}-400`}>
                <DynamicIcon name={icon || 'Activity'} className="h-6 w-6" />
            </div>
        </div>
        <h3 className="text-sm font-medium text-gray-500 dark:text-gray-400">{title}</h3>
        <div className="flex items-baseline gap-2 mt-1">
            <p className="text-3xl font-bold text-gray-900 dark:text-white">{value}</p>
            {subtext && <span className="text-xs text-gray-400">{subtext}</span>}
        </div>
    </motion.div>
);

// --- Section Card ---
const SectionCard = ({ title, children, delay, action }) => (
    <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.5, delay }}
        className="bg-white dark:bg-gray-800 p-6 rounded-xl shadow-sm border border-gray-100 dark:border-gray-700 h-full flex flex-col"
    >
        <div className="flex items-center justify-between mb-6">
            <h3 className="text-lg font-semibold text-gray-900 dark:text-white">{title}</h3>
            {action}
        </div>
        <div className="flex-1 overflow-auto custom-scrollbar">
            {children}
        </div>
    </motion.div>
);

// --- Action Item ---
const ActionItem = ({ action }) => {
    const { formatDate } = useTimezoneFormatter();
    return (
        <div className="flex items-start space-x-3 p-3 mb-3 bg-gray-50 dark:bg-gray-900 rounded-lg border border-gray-100 dark:border-gray-700 hover:border-blue-200 dark:hover:border-blue-800 transition-colors">
            <div className={`w-2 h-2 mt-2 rounded-full flex-shrink-0 ${action.priority === 'high' ? 'bg-red-500' : 'bg-blue-500'}`} />
            <div className="flex-1 min-w-0">
                <h4 className="text-sm font-semibold text-gray-900 dark:text-gray-100 truncate">
                    {action.title}
                </h4>
                <p className="text-xs text-gray-500 mt-1 line-clamp-2">
                    {action.description}
                </p>
                <div className="mt-2 flex items-center justify-between">
                    <span className="text-xs text-gray-400">{formatDate(action.created_at)}</span>
                    {action.action_link && (
                        <a
                            href={action.action_link}
                            className="px-3 py-1 text-xs font-medium text-blue-600 bg-blue-50 rounded-full hover:bg-blue-100 dark:bg-blue-900/30 dark:text-blue-300 transition-colors"
                        >
                            Resolve
                        </a>
                    )}
                </div>
            </div>
        </div>
    );
};

// --- Activity Item ---
const ActivityItem = ({ item }) => {
    const { formatDate } = useTimezoneFormatter();

    const typeStyles = {
        flow:      { dot: 'bg-blue-500',   badge: 'bg-blue-50 text-blue-700 dark:bg-blue-900/30 dark:text-blue-300' },
        dashboard: { dot: 'bg-purple-500', badge: 'bg-purple-50 text-purple-700 dark:bg-purple-900/30 dark:text-purple-300' },
        dataset:   { dot: 'bg-green-500',  badge: 'bg-green-50 text-green-700 dark:bg-green-900/30 dark:text-green-300' },
        alert:     { dot: 'bg-red-500',    badge: 'bg-red-50 text-red-700 dark:bg-red-900/30 dark:text-red-300' },
        user:      { dot: 'bg-yellow-500', badge: 'bg-yellow-50 text-yellow-700 dark:bg-yellow-900/30 dark:text-yellow-300' },
        default:   { dot: 'bg-gray-400',   badge: 'bg-gray-50 text-gray-600 dark:bg-gray-700 dark:text-gray-300' },
    };
    const style = typeStyles[item.type] || typeStyles.default;

    return (
        <div className="flex items-start space-x-3 py-3 border-b border-gray-100 dark:border-gray-700 last:border-0">
            <div className={`w-2 h-2 mt-1.5 rounded-full flex-shrink-0 ${style.dot}`} />
            <div className="flex-1 min-w-0">
                <p className="text-sm text-gray-800 dark:text-gray-200 leading-snug">
                    <span className="font-medium">
                        {item.action || item.title || item.name || 'Activity'}
                    </span>
                    {(item.entity_name || item.user) && (
                        <span className="text-gray-500">
                            {item.entity_name ? ` — ${item.entity_name}` : ''}
                            {item.user && item.user !== 'System' ? ` by ${item.user}` : ''}
                        </span>
                    )}
                </p>
                <div className="mt-1 flex items-center gap-2 flex-wrap">
                    {item.type && (
                        <span className={`inline-flex items-center px-2 py-0.5 rounded-full text-[10px] font-medium ${style.badge}`}>
                            {item.type}
                        </span>
                    )}
                    {item.status && (
                        <span className={`inline-flex items-center px-2 py-0.5 rounded-full text-[10px] font-medium
                            ${item.status === 'SUCCESS' || item.status === 'success'
                                ? 'bg-green-50 text-green-700 dark:bg-green-900/30 dark:text-green-300'
                                : item.status === 'FAILED' || item.status === 'failed'
                                ? 'bg-red-50 text-red-700 dark:bg-red-900/30 dark:text-red-300'
                                : 'bg-gray-50 text-gray-600 dark:bg-gray-700 dark:text-gray-300'
                            }`}>
                            {item.status}
                        </span>
                    )}
                    <span className="text-[10px] text-gray-400">
                        {formatDate(item.created_at || item.timestamp)}
                    </span>
                </div>
            </div>
        </div>
    );
};

// --- Welcome Banner ---
const WelcomeBanner = ({ user }) => (
    <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        className="relative overflow-hidden rounded-2xl bg-gradient-to-br from-indigo-50 via-blue-50 to-white dark:from-gray-800 dark:via-gray-800 dark:to-gray-900 p-8 shadow-sm border border-indigo-100 dark:border-gray-700 mb-8"
    >
        <div className="flex justify-between">
            <div>
                <h1 className="text-3xl font-bold">
                    Welcome back, {user?.name || 'User'}!
                </h1>
                <p className="mt-2 text-gray-600 dark:text-gray-400">
                    Design and orchestrate workflows efficiently.
                </p>
            </div>
        </div>
    </motion.div>
);

// --- Main Dashboard ---
export default function LandingDashboard({ setActivePage }) {
    const { user, token } = useAuth();
    const navigate = useNavigate();

    const [summary, setSummary]   = useState(null);
    const [actions, setActions]   = useState([]);
    const [activity, setActivity] = useState([]);
    const [loading, setLoading]   = useState(true);

    useEffect(() => {
        if (!token) return;
        Promise.all([
            getLandingSummary(token),
            getPendingActions(token),
            getRecentActivity(token),
        ])
            .then(([sumData, actData, recData]) => {
                setSummary(sumData);
                setActions(actData || []);
                setActivity(recData || []);
            })
            .catch(() => {})
            .finally(() => setLoading(false));
    }, [token]);

    if (loading) {
        return (
            <div className="flex h-64 items-center justify-center">
                <div className="w-6 h-6 border-2 border-blue-500 border-t-transparent rounded-full animate-spin" />
            </div>
        );
    }

    return (
        <div className="space-y-8 p-1">

            {/* Welcome Banner */}
            <WelcomeBanner user={user} />

            {/* ── Stat Tiles ── */}
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
                <StatCard title="ETL Health"    value={summary?.etl_status || '-'}      color="gray"   delay={0}    />
                <StatCard title="Active Models" value={summary?.active_ml_models || 0}  color="purple" delay={0.05} />
                <StatCard title="24h Workflows" value={summary?.workflow_runs_24h || 0}  color="blue"   delay={0.1}  />
                <StatCard title="24h API Calls" value={summary?.api_calls_24h || 0}     color="orange" delay={0.15} />
            </div>

            {/* ── Recent Activity + Actions Required ── */}
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">

                {/* Recent Activity */}
                <SectionCard
                    title="Recent Activity"
                    delay={0.2}
                    action={
                        <button
                            onClick={() => navigate('/activity-logs')}
                            className="text-xs text-blue-600 hover:text-blue-800 dark:text-blue-400 font-medium transition-colors"
                        >
                            View all →
                        </button>
                    }
                >
                    {activity.length === 0 ? (
                        <div className="flex flex-col items-center justify-center py-12 text-center">
                            <div className="w-12 h-12 rounded-full bg-gray-100 dark:bg-gray-700 flex items-center justify-center mb-3">
                                <svg className="w-5 h-5 text-gray-400" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z" />
                                </svg>
                            </div>
                            <p className="text-sm text-gray-500 dark:text-gray-400">No recent activity</p>
                            <p className="text-xs text-gray-400 mt-1">Actions will appear here as you use the platform</p>
                        </div>
                    ) : (
                        activity.map((item, i) => (
                            <ActivityItem key={item.id || i} item={item} />
                        ))
                    )}
                </SectionCard>

                {/* Actions Required */}
                <SectionCard
                    title="Actions Required"
                    delay={0.3}
                    action={
                        actions.length > 0 ? (
                            <span className="inline-flex items-center justify-center w-5 h-5 rounded-full bg-red-500 text-white text-[10px] font-bold">
                                {actions.length}
                            </span>
                        ) : null
                    }
                >
                    {actions.length === 0 ? (
                        <div className="flex flex-col items-center justify-center py-12 text-center">
                            <div className="w-12 h-12 rounded-full bg-green-50 dark:bg-green-900/20 flex items-center justify-center mb-3">
                                <svg className="w-5 h-5 text-green-500" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M5 13l4 4L19 7" />
                                </svg>
                            </div>
                            <p className="text-sm font-medium text-gray-700 dark:text-gray-300">All caught up!</p>
                            <p className="text-xs text-gray-400 mt-1">No pending actions right now</p>
                        </div>
                    ) : (
                        actions.map((action, i) => (
                            <ActionItem key={action.id || i} action={action} />
                        ))
                    )}
                </SectionCard>

            </div>
        </div>
    );
}